import { useCallback } from 'react'
import { api } from '@/lib/api'

export type Tier = 'free' | 'team' | 'scale' | 'enterprise'

// ── Subscriptions ──────────────────────────────────────────────────────────────
export interface SubscriptionRow {
  id: string
  name: string
  tier: Tier
  subscription_status: string
  subscription_current_period_end: string | null
  has_customer: boolean
  has_subscription: boolean
}

// ── Platform Storage ───────────────────────────────────────────────────────────
export interface AccountStorageData {
  organizationId: string
  name: string
  tier: string
  totalBytes: number
  buckets: {
    comparisons: number
    snapshots: number
    archives: number
    dataOps: number
    logs: number
  }
  computedAt: number
}

export interface PlatformStorageData {
  accounts: AccountStorageData[]
  grandTotalLogicalBytes: number
  physicalDatabaseBytes: number
  capacityBytes: number | null
  perTablePhysical: Array<{ table: string; bytes: number }>
  computedAt: number
}

// ── Audit ──────────────────────────────────────────────────────────────────────
export interface AuditEvent {
  id: string
  organization_id: string
  user_id: string
  action: string
  entity_type: string
  entity_id: string
  details: Record<string, unknown> | null
  created_at: string
  user_email: string
  org_name: string
}

export interface AuditParams {
  organizationId?: string
  userId?: string
  action?: string
  entityType?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

// ── Database Browser ──────────────────────────────────────────────────────────
export interface DbTableSummary {
  name: string
  rows: number
}

export interface DbColumn {
  column_name: string
  data_type: string
  is_nullable: string
}

export interface DbTableData {
  columns: DbColumn[]
  rows: Record<string, unknown>[]
  total: number
}

export interface DbQueryResult {
  success: true
  columns: string[]
  rows: Record<string, unknown>[]
  truncated: boolean
}

export interface DbQueryError {
  success: false
  message: string
}

// ── Connections (cross-org) ────────────────────────────────────────────────────
export interface GlobalConnection {
  id: string
  label: string
  status: string
  is_sandbox: boolean
  auth_type: string
  instance_url: string
  sf_org_id: string
  last_connected_at: string | null
  org_name: string
  organization_id: string
}

export interface ConnectionsParams {
  status?: string
  limit?: number
  offset?: number
}

// ── Health ─────────────────────────────────────────────────────────────────────
export type TestStatus = 'pass' | 'warn' | 'fail' | 'skip'

export interface TestResult {
  name: string
  status: TestStatus
  durationMs: number
  message?: string
  details?: unknown
}

export interface AreaResult {
  area: string
  status: TestStatus
  durationMs: number
  tests: TestResult[]
}

export interface RunSummary {
  pass: number
  warn: number
  fail: number
  skip: number
  totalTests: number
  totalAreas: number
}

export interface HealthRun {
  runId?: string
  startedAt?: string
  finishedAt?: string
  durationMs?: number
  status?: TestStatus
  summary?: RunSummary
  areas?: AreaResult[]
}

export interface ServicesStatus {
  database: boolean
  redis: boolean
  stripe: boolean
  email: boolean
  anthropic: boolean
  stripeWebhook: boolean
}

export interface HealthHistoryRow {
  id: string
  started_at: string
  finished_at: string | null
  duration_ms: number | null
  status: string
  summary: RunSummary | null
  triggered_by_email: string
}

export interface OrgRow {
  id: string
  name: string
  tier: Tier
  subscription_status: string
  created_at: string
  member_count: number
  connection_count: number
}

export interface OrgDetail {
  id: string
  name: string
  tier: Tier
  usage_insights_enabled: boolean
  subscription_status: string
  subscription_current_period_end: string | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
}

export interface OrgMember {
  user_id: string
  role: string
  email: string
  name: string
  created_at: string
}

export interface OrgConnection {
  id: string
  label: string
  status: string
  is_sandbox: boolean
  instance_url: string
  sf_org_id: string
  last_connected_at: string | null
}

export interface UserRow {
  id: string
  email: string
  name: string
  role: string
  organization_id: string | null
  is_platform_admin: boolean
  email_verified: boolean
  mfa_enabled: boolean
  created_at: string
}

export interface UserDetail extends UserRow {
  memberships: Array<{ organization_id: string; role: string; org_name: string }>
}

export function useAdmin() {
  const getOverview = useCallback(async () => {
    const { data } = await api.get('/api/admin/overview')
    return data as {
      counts: { organizations: number; users: number; connections: number }
      tierDistribution: Record<string, number>
      recentOrganizations: Array<{ id: string; name: string; tier: string; created_at: string }>
    }
  }, [])

  const listOrganizations = useCallback(
    async (params: { search?: string; tier?: string; limit?: number; offset?: number }) => {
      const { data } = await api.get('/api/admin/organizations', { params })
      return data as { organizations: OrgRow[]; total: number }
    },
    [],
  )

  const getOrganization = useCallback(async (id: string) => {
    const { data } = await api.get(`/api/admin/organizations/${id}`)
    return data as { organization: OrgDetail; members: OrgMember[]; connections: OrgConnection[] }
  }, [])

  const renameOrganization = useCallback(async (id: string, name: string) => {
    const { data } = await api.patch(`/api/admin/organizations/${id}`, { name })
    return data as { success: boolean }
  }, [])

  const setTier = useCallback(async (id: string, tier: Tier) => {
    const { data } = await api.patch(`/api/admin/organizations/${id}/tier`, { tier })
    return data as { success: boolean; tier: Tier }
  }, [])

  const setUsageInsights = useCallback(async (id: string, enabled: boolean) => {
    const { data } = await api.patch(`/api/admin/organizations/${id}/usage-insights`, { enabled })
    return data as { success: boolean; usageInsightsEnabled: boolean }
  }, [])

  const listUsers = useCallback(
    async (params: {
      search?: string
      platformAdmin?: boolean
      verified?: boolean
      mfa?: boolean
      limit?: number
      offset?: number
    }) => {
      const { data } = await api.get('/api/admin/users', { params })
      return data as { users: UserRow[]; total: number }
    },
    [],
  )

  const getUser = useCallback(async (id: string) => {
    // Backend returns { success, user: {...}, memberships: [...] } with memberships
    // at the TOP LEVEL, not nested inside `user`. Merge them so callers get a
    // single UserDetail object (dropping memberships crashes UserDetailPage).
    const { data } = await api.get(`/api/admin/users/${id}`)
    return { ...data.user, memberships: data.memberships ?? [] } as UserDetail
  }, [])

  const updateUser = useCallback(
    async (id: string, patch: { isPlatformAdmin?: boolean; name?: string }) => {
      const { data } = await api.patch(`/api/admin/users/${id}`, patch)
      return data as { success: boolean }
    },
    [],
  )

  const resetUserPassword = useCallback(async (id: string) => {
    const { data } = await api.post(`/api/admin/users/${id}/password-reset`)
    return data as { success: boolean }
  }, [])

  const listSubscriptions = useCallback(async () => {
    const { data } = await api.get('/api/admin/subscriptions')
    return data as { subscriptions: SubscriptionRow[] }
  }, [])

  const getPlatformStorage = useCallback(async () => {
    const { data } = await api.get('/api/storage/platform')
    return data as PlatformStorageData
  }, [])

  const listAudit = useCallback(async (params: AuditParams) => {
    const { data } = await api.get('/api/admin/audit', { params })
    return data as { events: AuditEvent[]; total: number }
  }, [])

  const listConnections = useCallback(async (params: ConnectionsParams) => {
    const { data } = await api.get('/api/admin/connections', { params })
    return data as { connections: GlobalConnection[]; total: number }
  }, [])

  const getHealthStatus = useCallback(async () => {
    const { data } = await api.get<{ services: ServicesStatus; connections: unknown }>(
      '/api/admin/health/status',
    )
    return data
  }, [])

  const runHealth = useCallback(async () => {
    const { data } = await api.post<{ run: HealthRun }>('/api/admin/health/run', {})
    return data
  }, [])

  const listHealthRuns = useCallback(async (limit = 10) => {
    const { data } = await api.get<{ runs: HealthHistoryRow[] }>(
      `/api/admin/health/runs?limit=${limit}`,
    )
    return data
  }, [])

  const getHealthRun = useCallback(async (id: string) => {
    const { data } = await api.get<{ run: HealthHistoryRow & { areas?: AreaResult[] } }>(
      `/api/admin/health/runs/${id}`,
    )
    return data
  }, [])

  const listTables = useCallback(async () => {
    const { data } = await api.get('/api/admin/database/tables')
    return data as { tables: DbTableSummary[] }
  }, [])

  const getTable = useCallback(
    async (name: string, params?: { limit?: number; offset?: number }) => {
      const { data } = await api.get(`/api/admin/database/tables/${name}`, { params })
      return data as DbTableData
    },
    [],
  )

  const runQuery = useCallback(async (sql: string) => {
    const { data } = await api.post('/api/admin/database/query', { sql })
    return data as DbQueryResult | DbQueryError
  }, [])

  const impersonate = useCallback(async (orgId: string) => {
    const { data } = await api.post(`/api/admin/organizations/${orgId}/impersonate`)
    return data as { redirectUrl: string }
  }, [])

  return {
    getOverview,
    listOrganizations,
    getOrganization,
    renameOrganization,
    setTier,
    setUsageInsights,
    listUsers,
    getUser,
    updateUser,
    resetUserPassword,
    listSubscriptions,
    getPlatformStorage,
    listAudit,
    listConnections,
    getHealthStatus,
    runHealth,
    listHealthRuns,
    getHealthRun,
    listTables,
    getTable,
    runQuery,
    impersonate,
  }
}
